import React, { useRef, useState } from 'react'
import { MerchantLocation } from '@slerp/controls'
import { Section } from 'components/Common'
import {
  OutlineButton,
  ConsentCard,
  ConsentText,
  SummaryActionCard,
  SummaryActions,
  SummaryText,
  LoyaltyAndTableGroup,
  CenteredCheckbox
} from './styles'
import {
  LINK_STORES_STEP_TITLE,
  LINK_STORES_STEP_DESCRIPTION,
  LOYALTY_CONSENT_DESCRIPTION_SLERP,
  marketingConsentLabel,
  linkedSummary
} from './copy'
import { isLeatConnected } from './utils'
import {
  ConnectStoresRow,
  LeatStore,
  LeatStoreLink,
  LoyaltyImplementation
} from './types'
import PushOrdersCard from './PushOrdersCard'
import StoreLinkTable from './StoreLinkTable'
import { useStoreLinkRows } from './useStoreLinkRows'
import LoadingPrimaryButton from './LoadingPrimaryButton'

interface ConnectStoresStepProps {
  activeStores: MerchantLocation[]
  existingLinks: LeatStoreLink[]
  leatShops: LeatStore[]
  customerCount: number | null
  loyaltyImplementation: LoyaltyImplementation | null
  loading?: boolean
  onConfirm: (rows: ConnectStoresRow[]) => void
  onBack?: () => void
}

const ConnectStoresStep = ({
  activeStores,
  existingLinks,
  leatShops,
  customerCount,
  loyaltyImplementation,
  loading,
  onConfirm,
  onBack
}: ConnectStoresStepProps) => {
  // An existing link is edited from the store list, not here.
  const unconnectedStores = activeStores.filter(
    (store) => !isLeatConnected(store.id, existingLinks)
  )
  const alreadyConnectedCount = activeStores.length - unconnectedStores.length

  const { rows, updateRow } = useStoreLinkRows(() =>
    unconnectedStores.reduce<Record<string, ConnectStoresRow>>((acc, store) => {
      acc[store.id] = {
        storeId: store.id,
        leatStoreId: null,
        // An unconnected store can still hold a stale
        // leat_order_push_enabled, so the stored value isn't read back.
        leatOrderPushEnabled: true
      }
      return acc
    }, {})
  )
  const [marketingChecked, setMarketingChecked] = useState(true)
  const [isConsentHighlighted, setIsConsentHighlighted] = useState(false)
  const consentRef = useRef<HTMLDivElement>(null)
  const [loyaltyImplementationAtMount] = useState(loyaltyImplementation)

  const rowList = unconnectedStores.map((store) => rows[store.id])
  const linkedCount =
    alreadyConnectedCount + rowList.filter((row) => row.leatStoreId).length
  const pushDisabledCount = rowList.filter(
    (row) => !row.leatOrderPushEnabled
  ).length

  const isSwitchingLoyalty =
    loyaltyImplementationAtMount !== LoyaltyImplementation.LEAT
  const hasAnyShopSelected = rowList.some((row) => row.leatStoreId)

  // A disabled CTA can't explain itself from the far end of a long store list.
  const handleConfirm = () => {
    if (!marketingChecked) {
      setIsConsentHighlighted(true)
      consentRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
      return
    }
    onConfirm(rowList)
  }

  return (
    <>
      <Section
        title={LINK_STORES_STEP_TITLE}
        description={LINK_STORES_STEP_DESCRIPTION}
      >
        <LoyaltyAndTableGroup>
          <PushOrdersCard />

          <StoreLinkTable
            stores={unconnectedStores}
            rows={rows}
            leatShops={leatShops}
            loading={loading}
            onRowChange={updateRow}
          />
        </LoyaltyAndTableGroup>

        {isSwitchingLoyalty && (
          <ConsentCard>
            <ConsentText>{LOYALTY_CONSENT_DESCRIPTION_SLERP}</ConsentText>
          </ConsentCard>
        )}

        <div ref={consentRef} data-testid='marketing-consent'>
          <CenteredCheckbox
            $inputMarginBottom={8}
            $highlighted={isConsentHighlighted}
            disabled={loading}
            checked={marketingChecked}
            onChange={(e) => {
              setMarketingChecked(e.target.checked)
              if (e.target.checked) setIsConsentHighlighted(false)
            }}
          >
            {marketingConsentLabel(customerCount ?? 0)}
          </CenteredCheckbox>
        </div>
      </Section>

      <SummaryActionCard>
        <SummaryText>
          {linkedSummary(linkedCount, activeStores.length, pushDisabledCount)}
        </SummaryText>
        <SummaryActions>
          {onBack && (
            <OutlineButton disabled={loading} onClick={onBack}>
              BACK
            </OutlineButton>
          )}
          <LoadingPrimaryButton
            disabled={!hasAnyShopSelected || loading}
            loading={loading}
            onClick={handleConfirm}
          >
            CONFIRM &amp; CONNECT
          </LoadingPrimaryButton>
        </SummaryActions>
      </SummaryActionCard>
    </>
  )
}

export default ConnectStoresStep
